import { TranslationsTarget } from '../enums';
import { languages, languagesWithoutNL, userLanguages } from './languages';

/**
 * Locales that actually have a bundled JSON file per namespace.
 *
 * The admin Translations tab fetches `/translations/<target>/<lang>.json`, so
 * offering a locale with no file behind it only produces a 404 and an error
 * toast — `ru` has no `web` file, and `guest` is translated into four locales
 * only. This map is the sole gate on what the tab offers: it is neither a
 * subset nor a superset of the app's user-selectable languages, which is why
 * intersecting the two used to drop translatable locales.
 *
 * Keep in sync with the translation directories — `pnpm check:translations`
 * fails when this map and the files on disk disagree:
 *   WEB     → apps/seaters/public/translations/web
 *   COCKPIT → apps/seaters/public/translations/cockpit
 *   GUEST   → apps/invitation/public/translations/guest
 */
export const translationsTargetLocales: Record<TranslationsTarget, string[]> = {
  [TranslationsTarget.WEB]: [
    'cn',
    'de',
    'en',
    'es',
    'fr',
    'it',
    'ja',
    'nl_BE',
    'nl_NL',
    'pt',
  ],
  [TranslationsTarget.COCKPIT]: ['en', 'fr', 'nl', 'nl_BE'],
  [TranslationsTarget.GUEST]: ['en', 'fr', 'nl_BE', 'nl_NL'],
};

/**
 * Display names for every locale a namespace can bundle.
 *
 * The app's own language lists cannot be used as the gate here, only as a label
 * source: `languages` and `userLanguages` both omit `de`, `userLanguages`
 * replaces plain `nl` with nl_BE/nl_NL, and no list carries `pt`. Filtering the
 * tab through `userLanguagesOptions` therefore hid `de`/`pt` (web) and `nl`
 * (cockpit) — locales that are translated and editable — so the bundled-locale
 * map above is now the only thing that decides what the tab offers.
 */
const localeLabels: Record<string, string> = {
  ...Object.fromEntries(
    [...languagesWithoutNL, ...languages, ...userLanguages].map((language) => [
      language.locale,
      language.name['en'],
    ])
  ),
  // `pt` ships a web JSON but is not a selectable app language, so no entry exists.
  pt: 'Português',
};

const getLocaleLabel = (locale: string) =>
  localeLabels[locale] ?? locale.toUpperCase();

// English is the base every namespace is translated from, so it leads the list
// and is what the tab clamps to; the rest sort by the name the editor reads.
const compareLocales = (a: string, b: string) => {
  // Equal locales must compare 0 — a comparator that never reports equality is
  // not a total order, and sort() is free to return anything for one.
  if (a === b) {
    return 0;
  }

  if (a === 'en' || b === 'en') {
    return a === 'en' ? -1 : 1;
  }

  return getLocaleLabel(a).localeCompare(getLocaleLabel(b));
};

/**
 * The languages the given namespace actually bundles, English first.
 */
export const getTranslationsTargetLanguageOptions = (
  target: TranslationsTarget
) =>
  [...(translationsTargetLocales[target] ?? [])]
    .sort(compareLocales)
    .map((locale) => ({ label: getLocaleLabel(locale), value: locale }));

/** Locale to fall back to when the current one is unavailable for a namespace. */
export const getDefaultTranslationsTargetLanguage = (
  target: TranslationsTarget
) => getTranslationsTargetLanguageOptions(target)[0]?.value;
